/**
 * Testimonials Carousel Component
 *
 * Auto-rotating carousel for displaying customer testimonials
 * with manual navigation controls and dot indicators.
 */

import { useState, useEffect } from 'react';
import type { Testimonial } from '../../types/testimonial';

interface TestimonialsCarouselProps {
  testimonials: Testimonial[];
  autoAdvanceInterval?: number; // milliseconds, default 5000
}

export const TestimonialsCarousel: React.FC<TestimonialsCarouselProps> = ({
  testimonials,
  autoAdvanceInterval = 5000
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-advance with cleanup to prevent memory leak
  useEffect(() => {
    if (testimonials.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, autoAdvanceInterval);

    return () => clearInterval(interval); // Cleanup on unmount
  }, [testimonials.length, autoAdvanceInterval]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  const currentTestimonial = testimonials[currentIndex];

  // Helper: Render star rating
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? 'text-yellow-400' : 'text-gray-300'}>
        ★
      </span>
    ));
  };

  return (
    <section className="py-16 md:py-24 bg-timberwolf-100">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2
          className="font-display text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider text-center mb-12 text-white"
          data-aos="fade-down"
        >
          WHAT STUDENTS SAY
        </h2>

        {/* Testimonial Card: Brutalist stocky design */}
        <div
          className="bg-white border-4 border-rich_black-500 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] p-8 md:p-12 relative transition-all duration-500 ease-out"
          data-aos="zoom-in"
        >
          {/* Stars: Large and bold */}
          <div className="flex justify-center mb-6 text-3xl gap-1">
            {renderStars(currentTestimonial.rating)}
          </div>

          {/* Quote: Large, bold text */}
          <blockquote className="font-sans text-xl md:text-2xl font-semibold text-gray-700 text-center mb-8">
            "{currentTestimonial.quote}"
          </blockquote>

          {/* Attribution: Geometric border accent */}
          <div className="text-center pt-6 border-t-4 border-amaranth_purple-500">
            <p className="font-sans font-bold text-lg md:text-xl text-gray-900 mb-1">
              {currentTestimonial.name}
            </p>
            <p className="font-mono text-xs uppercase tracking-wider text-gray-600">
              {currentTestimonial.location} • {currentTestimonial.courseTaken}
            </p>
          </div>

          {/* Navigation Buttons: Brutalist square buttons */}
          {testimonials.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white border-4 border-rich_black-500 w-12 h-12 flex items-center justify-center shadow-[4px_4px_0px_0px_rgba(2,5,18,1)] hover:shadow-[6px_6px_0px_0px_rgba(2,5,18,1)] hover:-translate-x-1 hover:-translate-y-1/2 transition-all duration-200 font-bold text-2xl"
                aria-label="Previous testimonial"
              >
                ‹
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white border-4 border-rich_black-500 w-12 h-12 flex items-center justify-center shadow-[4px_4px_0px_0px_rgba(2,5,18,1)] hover:shadow-[6px_6px_0px_0px_rgba(2,5,18,1)] hover:translate-x-1 hover:-translate-y-1/2 transition-all duration-200 font-bold text-2xl"
                aria-label="Next testimonial"
              >
                ›
              </button>
            </>
          )}
        </div>

        {/* Dots Indicator: Square geometric dots */}
        {testimonials.length > 1 && (
          <div className="flex justify-center gap-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToIndex(index)}
                className={`w-4 h-4 border-2 transition-all duration-200 ${
                  index === currentIndex
                    ? 'bg-amaranth_purple-500 border-amaranth_purple-700'
                    : 'bg-white border-gray-400 hover:border-gray-600'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
